import clsx from "clsx";
import useLogout from "common/auth/hooks/use-logout";
import { CreditCardIcon } from "icons/credit-card-icon";
import { GearIcon } from "icons/gear-icon";
import { LogOutIcon } from "icons/log-out-icon";
import { UsersIcon } from "icons/users-icon";
import React, {
  cloneElement,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import paths from "routes/paths";
import { useAppSelector } from "store";
import { authNewSelectors } from "store/reducers/auth-new";

import css from "./index.module.scss";

type NavItem = {
  icon: ReactElement;
  label: string;
  path: string;
};

export const UserMenu = () => {
  const [open, openSet] = useState(false);
  const isTeamOwner = useAppSelector(authNewSelectors.userIsTeamOwner);
  const logout = useLogout();
  const name = useAppSelector(authNewSelectors.userNameGet);
  const ref = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        openSet(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open]);

  const NAV_USER: NavItem[] = [];
  if (isTeamOwner) {
    NAV_USER.push({
      icon: <UsersIcon />,
      label: "Team",
      path: paths.settingsTeam(),
    });
    NAV_USER.push({
      icon: <CreditCardIcon />,
      label: "Billing",
      path: paths.settingsBilling(),
    });
  }
  NAV_USER.push({
    icon: <GearIcon />,
    label: "Account settings",
    path: paths.settingsAccount(),
  });

  return (
    <div className={css.userMenu}>
      {open && (
        <div className={css.nav}>
          {NAV_USER.map((i) => (
            <Link
              className={clsx(css.item, i.path === pathname && css.active)}
              key={i.label}
              to={i.path}
            >
              {cloneElement(i.icon, { className: css.itemIcon })}
              {i.label}
            </Link>
          ))}
          <a className={css.item} onClick={() => logout()}>
            <LogOutIcon className={css.itemIcon} />
            Log out
          </a>
        </div>
      )}
      <button
        className={clsx(css.userMenuButton, open && css.open)}
        ref={ref}
        onClick={() => openSet(!open)}
      >
        <svg
          className={css.avatar}
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 0C4.02891 0 0 4.02891 0 9C0 13.9711 4.02891 18 9 18C13.9711 18 18 13.9711 18 9C18 4.02891 13.9711 0 9 0ZM9 4.5C10.3982 4.5 11.5312 5.63344 11.5312 7.03125C11.5312 8.42906 10.3992 9.5625 9 9.5625C7.60219 9.5625 6.46875 8.42906 6.46875 7.03125C6.46875 5.63344 7.60078 4.5 9 4.5ZM9 15.75C7.13918 15.75 5.45273 14.9931 4.2293 13.7711C4.79883 12.3012 6.20508 11.25 7.875 11.25H10.125C11.7963 11.25 13.2026 12.3005 13.7707 13.7711C12.5473 14.9941 10.8598 15.75 9 15.75Z" />
        </svg>
        <span className={css.username}>{name}</span>
        <svg
          className={css.arrow}
          style={{ transform: `rotate(180deg)` }}
          viewBox="0 0 448 512"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z" />
        </svg>
      </button>
    </div>
  );
};
